import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Gift, Copy, Check as CheckIcon, Share2, Loader2 } from 'lucide-react';

/**
 * Pure presentational Refer & Earn card for the Profile page.
 *
 * Owns NO state — parent owns `applyCode`, `applying`, `referralCopied`,
 * copy/share/apply handlers, and `user.referredBy` visibility. This makes
 * the component trivially testable and avoids scattering referral state
 * across multiple files.
 */
export const ProfileReferralCard = ({
  referral,
  referralLink,
  referralCopied,
  applyCode,
  applying,
  applyError,
  applyMsg,
  userReferredBy,
  onCopy,
  onShare,
  onApplyCodeChange,
  onApplyCode,
}) => {
  if (!referral) return null;

  return (
    <Card
      className="bg-[#1A1A1A] border-gray-800 mb-6"
      data-testid="profile-referral-card"
    >
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            <Gift className="w-5 h-5 text-[#00FF00]" />
            Refer & Earn
          </CardTitle>
          <Badge className="bg-[#00FF00]/15 text-[#00FF00] border-[#00FF00]/40">
            +{referral.bonus_days_per_conversion} days each
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-gray-300 text-sm">
          Invite a friend. When they start a Pro trial,{' '}
          <span className="text-[#00FF00] font-semibold">both of you</span> get{' '}
          {referral.bonus_days_per_conversion} extra days of Pro — forever on us.
        </p>

        {/* Referral code + copy */}
        <div>
          <label className="text-xs text-gray-500 uppercase tracking-wide">
            Your referral code
          </label>
          <div className="mt-2 flex flex-col sm:flex-row gap-2">
            <div
              className="flex-1 bg-[#0A0A0A] border border-gray-800 rounded-md px-4 py-3 font-mono text-[#00FF00] tracking-widest text-lg select-all"
              data-testid="referral-code-display"
            >
              {referral.code}
            </div>
            <Button
              onClick={onCopy}
              className="bg-[#00FF00] text-black hover:bg-[#00DD00] font-semibold"
              data-testid="referral-copy-btn"
            >
              {referralCopied ? (
                <>
                  <CheckIcon className="w-4 h-4 mr-2" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy link
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={onShare}
              className="border-gray-700 text-gray-200 hover:bg-gray-800"
              data-testid="referral-share-btn"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-2 break-all" data-testid="referral-link">
            {referralLink}
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-[#0A0A0A] border border-gray-800 rounded-md p-3">
            <p className="text-xs text-gray-500">Signed up</p>
            <p className="text-2xl font-bold text-white" data-testid="referral-signed-up">
              {referral.referred_count}
            </p>
          </div>
          <div className="bg-[#0A0A0A] border border-gray-800 rounded-md p-3">
            <p className="text-xs text-gray-500">Converted</p>
            <p
              className="text-2xl font-bold text-[#00FF00]"
              data-testid="referral-converted"
            >
              {referral.converted_count}
            </p>
          </div>
          <div className="bg-[#0A0A0A] border border-gray-800 rounded-md p-3">
            <p className="text-xs text-gray-500">Bonus earned</p>
            <p
              className="text-2xl font-bold text-white"
              data-testid="referral-bonus-days"
            >
              {referral.bonus_days_earned}
              <span className="text-sm text-gray-500 font-medium"> days</span>
            </p>
          </div>
        </div>

        {/* Apply incoming code (only if user hasn't been referred yet) */}
        {!userReferredBy && (
          <div className="border-t border-gray-800 pt-4">
            <label className="text-xs text-gray-500 uppercase tracking-wide">
              Have a friend's code?
            </label>
            <div className="mt-2 flex flex-col sm:flex-row gap-2">
              <Input
                value={applyCode}
                onChange={(e) => onApplyCodeChange(e.target.value)}
                placeholder="Enter referral code"
                className="flex-1 bg-[#0A0A0A] border-gray-800 text-white placeholder:text-gray-500"
                data-testid="referral-apply-input"
              />
              <Button
                onClick={onApplyCode}
                disabled={applying}
                className="bg-[#00BFFF] text-black hover:bg-[#00A3DD] font-semibold"
                data-testid="referral-apply-btn"
              >
                {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply code'}
              </Button>
            </div>
            {applyError && (
              <p className="text-sm text-red-400 mt-2" data-testid="referral-apply-error">
                {applyError}
              </p>
            )}
            {applyMsg && (
              <p
                className="text-sm text-[#00FF00] mt-2"
                data-testid="referral-apply-success"
              >
                {applyMsg}
              </p>
            )}
          </div>
        )}

        {userReferredBy && (
          <p className="text-xs text-gray-500">
            Referred by{' '}
            <span className="text-[#00FF00] font-mono">{userReferredBy}</span>. Start your
            Pro trial to unlock your bonus days.
          </p>
        )}
      </CardContent>
    </Card>
  );
};
